import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useTour, TourStep } from "../contexts/TourContext";

interface SpotlightPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TooltipPosition {
  top: number;
  left: number;
}

export const AppTour: React.FC = () => {
  const {
    isOpen,
    currentStep,
    steps,
    nextStep,
    prevStep,
    endTour,
    markTourComplete,
  } = useTour();

  const [spotlightPosition, setSpotlightPosition] = useState<SpotlightPosition | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({ top: 0, left: 0 });
  const [isAnimating, setIsAnimating] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const currentStepData: TourStep | undefined = steps[currentStep];

  // Calculate spotlight and tooltip positions
  const updatePositions = useCallback(() => {
    if (!currentStepData) return;

    const { target, placement = "bottom", highlightPadding = 8 } = currentStepData;

    // For center placement (welcome/complete screens), no spotlight
    if (placement === "center" || target === "body") {
      setSpotlightPosition(null);
      setTooltipPosition({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
      });
      return;
    }

    // Find the target element
    const element = document.querySelector(target);
    if (!element) {
      // Element not found, use center position
      setSpotlightPosition(null);
      setTooltipPosition({
        top: window.innerHeight / 2,
        left: window.innerWidth / 2,
      });
      return;
    }

    const rect = element.getBoundingClientRect();
    
    // Set spotlight position with padding
    setSpotlightPosition({
      top: rect.top - highlightPadding,
      left: rect.left - highlightPadding,
      width: rect.width + highlightPadding * 2,
      height: rect.height + highlightPadding * 2,
    });

    // Calculate tooltip position based on placement
    const tooltipWidth = 420;
    const tooltipHeight = 320; // Increased for longer content
    const margin = 16;

    let top = 0;
    let left = 0;

    switch (placement) {
      case "top":
        top = rect.top - tooltipHeight - margin;
        left = rect.left + rect.width / 2;
        break;
      case "bottom":
        top = rect.bottom + margin;
        left = rect.left + rect.width / 2;
        break;
      case "left":
        top = rect.top + rect.height / 2;
        left = rect.left - tooltipWidth - margin;
        break;
      case "right":
        top = rect.top + rect.height / 2;
        left = rect.right + margin;
        break;
      default:
        top = rect.bottom + margin;
        left = rect.left + rect.width / 2;
    }

    // Ensure tooltip stays within viewport
    const viewportPadding = 20;
    
    // Horizontal bounds
    if (left < viewportPadding) {
      left = viewportPadding;
    } else if (left + tooltipWidth > window.innerWidth - viewportPadding) {
      left = window.innerWidth - tooltipWidth - viewportPadding;
    }
    
    // Vertical bounds
    if (top < viewportPadding) {
      top = viewportPadding;
    } else if (top + tooltipHeight > window.innerHeight - viewportPadding) {
      top = window.innerHeight - tooltipHeight - viewportPadding;
    }

    setTooltipPosition({ top, left });
  }, [currentStepData]);

  // Update positions when step changes or window resizes
  useEffect(() => {
    if (!isOpen) return;

    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 300);

    updatePositions();

    const handleResize = () => updatePositions();
    const handleScroll = () => updatePositions();

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, currentStep, updatePositions]);

  // Scroll target element into view
  useEffect(() => {
    if (!isOpen || !currentStepData) return;

    const { target, placement } = currentStepData;
    if (placement === "center" || target === "body") return;

    const element = document.querySelector(target);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }
  }, [isOpen, currentStep, currentStepData]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowRight":
        case "Enter":
          e.preventDefault();
          nextStep();
          break;
        case "ArrowLeft":
          e.preventDefault();
          prevStep();
          break;
        case "Escape":
          e.preventDefault();
          endTour();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, nextStep, prevStep, endTour]);

  if (!isOpen || !currentStepData) return null;

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const isCenterPlacement = currentStepData.placement === "center" || currentStepData.target === "body";
  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleSkip = () => {
    markTourComplete();
    endTour();
  };

  return createPortal(
    <div className="fixed inset-0 z-[100000]" role="dialog" aria-modal="true" aria-label="Application Tour">
      {/* Overlay with spotlight cutout */}
      <div className="absolute inset-0">
        {/* Dark overlay */}
        <div
          className="absolute inset-0 bg-black/60 transition-opacity duration-300"
          style={{ opacity: isAnimating ? 0.3 : 0.6 }}
          onClick={handleSkip}
        />
        
        {/* Spotlight cutout */}
        {spotlightPosition && (
          <div
            className="absolute bg-transparent transition-all duration-300 ease-out"
            style={{
              top: spotlightPosition.top,
              left: spotlightPosition.left,
              width: spotlightPosition.width,
              height: spotlightPosition.height,
              boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.6)",
              borderRadius: "8px",
              pointerEvents: currentStepData.spotlightClicks ? "none" : "auto",
            }}
          >
            {/* Animated border */}
            <div
              className="absolute inset-0 rounded-lg animate-pulse"
              style={{
                border: "2px solid var(--color-primary-500, #35ba5b)",
                boxShadow: "0 0 20px var(--color-primary-500, #35ba5b)",
              }}
            />
          </div>
        )}
      </div>

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`absolute z-[100001] w-[420px] max-w-[90vw] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden transition-all duration-300 ${
          isAnimating ? "opacity-0 scale-95" : "opacity-100 scale-100"
        }`}
        style={{
          top: isCenterPlacement ? "50%" : tooltipPosition.top,
          left: isCenterPlacement ? "50%" : tooltipPosition.left,
          transform: isCenterPlacement
            ? "translate(-50%, -50%)"
            : currentStepData.placement === "top" || currentStepData.placement === "bottom"
            ? "translateX(-50%)"
            : currentStepData.placement === "left" || currentStepData.placement === "right"
            ? "translateY(-50%)"
            : "none",
        }}
      >
        {/* Progress bar */}
        <div className="h-1 bg-gray-200 dark:bg-gray-700">
          <div
            className="h-full transition-all duration-500 ease-out"
            style={{
              width: `${progress}%`,
              backgroundColor: "var(--color-primary-500, #35ba5b)",
            }}
          />
        </div>

        {/* Content */}
        <div className="p-5">
          {/* Step indicator */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Step {currentStep + 1} of {steps.length}
            </span>
            <button
              onClick={handleSkip}
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              Skip Tour
            </button>
          </div>

          {/* Title */}
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
            {currentStepData.title}
          </h3>

          {/* Description - with proper formatting for multi-line content */}
          <div className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-5 max-h-[280px] overflow-y-auto custom-scrollbar">
            {currentStepData.content.split('\n').map((line, index) => {
              // Check if line is a bullet point
              if (line.trim().startsWith('•')) {
                return (
                  <div key={index} className="flex items-start gap-2 ml-1 my-0.5">
                    <span className="text-primary-500 mt-0.5">•</span>
                    <span>{line.trim().substring(1).trim()}</span>
                  </div>
                );
              }
              // Empty line creates spacing
              if (line.trim() === '') {
                return <div key={index} className="h-2" />;
              }
              // Regular text
              return <p key={index} className="my-1">{line}</p>;
            })}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={prevStep}
              disabled={isFirstStep}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                isFirstStep
                  ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                  : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600"
              }`}
            >
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </span>
            </button>

            {/* Step progress indicator - compact for many steps */}
            <div className="flex items-center gap-1 px-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">
                {currentStep + 1}/{steps.length}
              </span>
              <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary-500 transition-all duration-300 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            <button
              onClick={nextStep}
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: "var(--color-primary-500, #35ba5b)" }}
            >
              <span className="flex items-center gap-1">
                {isLastStep ? "Finish" : "Next"}
                {!isLastStep && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                )}
                {isLastStep && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </span>
            </button>
          </div>
        </div>

        {/* Keyboard hint */}
        <div className="px-5 py-2.5 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Use <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 text-xs font-mono">←</kbd>{" "}
            <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 text-xs font-mono">→</kbd> to navigate,{" "}
            <kbd className="px-1.5 py-0.5 bg-white dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 text-xs font-mono">Esc</kbd> to exit
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AppTour;
